import React from "react";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import Drawer from "js/common/views/drawer";
import Hint from "js/admin/common/hint";
import getLibraryOfComponents from "js/dashboards/components/prebuilt-component-library";
import AiAssistantButton from "js/dashboards/toolbar/ai-assistant-button";
import currentClient from "js/common/repo/backbone/current-client";
import {clientPermissions} from "js/common/permissions";
import * as Branding from "js/common/branding-constants";
import * as Auditor from "js/common/auditor";

const Header = React.memo(({text}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return <div
      style={{
        fontWeight: "500",
        padding: "10px 15px 0 15px",
        marginTop: 10,
        lineHeight: 1,
        fontSize: "13px",
        color: theme.palette.text.main
      }}>{text}</div>;
});

const Item = React.memo(({text, description, category, onClick = () => {}}) => {
  const {theme} = React.useContext(CustomThemeContext);

  const iconMap = {
    "Metric": "fa fa-circle-o-notch",
    "Ratio": "fa fa-percent",
    "RatioComparison": "fa fa-percent",
    "VelocityComparison": "bhi-bolt",
    "PerformanceSnapshot": "fa fa-th-large",
    "SmallData": "fa fa-th-large",
    "BigData": "fa fa-th",
    "Trend": "fa fa-line-chart",
    "Heading": "fa fa-paragraph",
    "Divider": "fa fa-grip-lines",
    "Leaderboard": "fa fa-trophy"
  };

  const backgroundMap = {
    "Metrics": theme.palette.primary.main,
    "Grids": theme.palette.warning.main,
    "Charts": "#5BDDF7",
    "Layout": "#44BB77",
    "Gamification": "#C964B7"
  };

  const iconLookup = text.split(" ").slice(0, 2).join("");

  return <div
      data-test-id="prebuilt-panel"
      onClick={onClick}
      style={{
        cursor: "pointer",
        backgroundColor: theme.themeId === "light" ? theme.palette.background.card : theme.palette.background.paper,
        boxShadow: "0 0 3px rgba(0,0,0,0.15)",
        borderRadius: "5px",
        padding: "9px",
        minWidth: "100%",
        maxWidth: "100%",
        margin: "10px 0 0 0",
        fontSize: "13px",
        display: "flex",
        alignItems: "center"
      }}>
    <span
        style={{
          margin: "0 10px 0 0",
          position: "relative",
          width: 36,
          minWidth: 36,
          height: 36,
          lineHeight: "36px",
          textAlign: "center"
        }}>
      <span
          style={{
            background: backgroundMap[category],
            borderRadius: "5px",
            opacity: 0.2,
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%"
          }} />
      <i
          className={iconMap[iconLookup]}
          style={{position: "relative", top: 2, fontSize: 18, color: backgroundMap[category]}} />
    </span>
    <div>
      {text}
      <span style={{display: "block", fontSize: 10, opacity: 0.5}}>{description}</span>
    </div>
  </div>;
});

const AddComponentDrawer = React.memo(({open, onRequestClose, onRequestOpenAiAssistant, onAddComponent}) => {
  const categoryToComponents = getLibraryOfComponents().groupBy(c => c.get("category"));
  const hasAiAssistant = currentClient.hasPermission(clientPermissions.canUseAiAssistant);

  const openAiAssistant = () => {
    Auditor.audit("ai-assistant-clicked", {from: "dashboards-add-panel"});
    onRequestOpenAiAssistant();
  };

  return <Drawer
      dataProductId={open ? "add-panel-drawer-open" : "add-panel-drawer-closed"}
      dataTestId={open ? "add-panel-drawer-open" : "add-panel-drawer-closed"}
      width={400}
      onRequestClose={onRequestClose}
      open={open}
      title="Add Panel"
      showCloseButton
      openRight={true}>

    {hasAiAssistant && <Hint style={{margin: "10px 10px 0 10px", alignItems: "flex-start"}} icon="magic">
      <div
          style={{display: "flex", flexDirection: "column", width: "100%", marginTop: 4}}
          data-test-id="ai-assistant-hint">
        <p style={{fontSize: "1em"}}>
          Need a panel fast? Just tell {Branding.aiAssistantName} what you want.
        </p>
        <AiAssistantButton onClick={openAiAssistant} style={{alignSelf: "flex-end"}} />
      </div>
    </Hint>}

    {categoryToComponents
        .entrySeq()
        .map(([category, componentsForCategory]) => {
          return <React.Fragment key={category}>
            <Header text={category} />
            <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  padding: "5px 15px 0 15px"
                }}>
              {componentsForCategory.map(component => <Item
                  key={component.get("label")}
                  text={component.get("label")}
                  description={component.get("description")}
                  category={category}
                  onClick={() => onAddComponent(component.get("componentConfig"))} />)}
            </div>
          </React.Fragment>;
        })}
  </Drawer>;
});

export default AddComponentDrawer;
